import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShoppingCart, Plus, Minus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ModifierDialog from "@/components/ModifierDialog";
import AIWaiterVoiceChat from "@/components/AIWaiterVoiceChat";
import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CustomerOrder() {
  const getVisibleCount = () => {
    if (window.innerWidth < 640) return 2; // mobile
    if (window.innerWidth < 768) return 3; // small tablet
    if (window.innerWidth < 1280) return 6; // tablet
    return 8; // desktop
  };

  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const [startIndex, setStartIndex] = useState(0);
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const existingOrderId = searchParams.get("existing_order");
  const [isChecked, setIsChecked] = useState(false);
  const [users, setUsers] = useState([]);
  const [table, setTable] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState("all");
  const [modifierDialogOpen, setModifierDialogOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [itemNoteForModifier, setItemNoteForModifier] = useState("");
  const [upsellDialogOpen, setUpsellDialogOpen] = useState(false);
  const [upsellOffers, setUpsellOffers] = useState([]);

  useEffect(() => {
    fetchTable();
    fetchMenuItems();
    fetchCategories();
  }, [tableId]);

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleCount());
      setStartIndex(0); // reset so UI never breaks
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchTable = async () => {
    try {
      const response = await axios.get(`${API}/public/tables/${tableId}`);
      setTable(response.data);
    } catch (error) {
      console.error("Error fetching table:", error);
      toast.error("Invalid table or table not found");
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(
        `${API}/public/menu/items?table_id=${tableId}&available_only=true`
      );
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching menu:", error);
      toast.error("Unable to load menu");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API}/public/categories?table_id=${tableId}`
      );
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Unable to load categories");
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await axios.get(
        `${API}/subcategories?category_id=${categoryId}`
      );
      setSubCategories(response.data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubCategories([]);
    }
  };

  useEffect(() => {
    if (selectedCategory !== "all") {
      fetchSubCategories(selectedCategory);
      setSelectedSubCategory("all");
    } else {
      setSubCategories([]);
      setSelectedSubCategory("all");
    }
  }, [selectedCategory]);

  const addToCart = (item) => {
    if (
      (item.modifier_groups && item.modifier_groups.length > 0) ||
      (item.add_ons && item.add_ons.length > 0)
    ) {
      setSelectedMenuItem(item);
      setSelectedModifiers({});
      setSelectedAddOns([]);
      setItemNoteForModifier("");
      setModifierDialogOpen(true);
      return;
    }

    const existingItem = cart.find((cartItem) => cartItem.id === item.id);
    if (existingItem) {
      updateQuantity(item.id, existingItem.quantity + 1);
    } else {
      setCart([
        ...cart,
        { ...item, quantity: 1, notes: "", modifiers: [], add_ons: [] },
      ]);
    }
    toast.success(`${item.name} added to cart`);
  };

  const addToCartWithModifiers = (item, modifiers, addOns, notes) => {
    const cartItem = {
      ...item,
      quantity: 1,
      modifiers: modifiers,
      add_ons: addOns,
      notes: notes,
      cart_item_id: Date.now(),
    };
    setCart([...cart, cartItem]);
    toast.success(`${item.name} added to cart with customizations`);
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity === 0) {
      setCart(cart.filter((item) => item.id !== id));
    } else {
      setCart(
        cart.map((item) =>
          item.id === id ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  const removeFromCart = (cartItemId) => {
    setCart((prevCart) =>
      prevCart.filter((item) => (item.cart_item_id || item.id) !== cartItemId)
    );
  };

  const getItemPrice = (item) => {
    return item.pricing?.dine_in || item.price || 0;
  };

  const calculateCartTotal = () => {
    return cart.reduce((total, item) => {
      let itemPrice = getItemPrice(item);

      if (item.modifiers && item.modifiers.length > 0) {
        item.modifiers.forEach((mod) => {
          itemPrice += mod.option_price || 0;
        });
      }

      if (item.add_ons && item.add_ons.length > 0) {
        item.add_ons.forEach((addon) => {
          itemPrice += addon.price || 0;
        });
      }

      return total + itemPrice * item.quantity;
    }, 0);
  };

  const placeOrder = async (skipUpsellCheck = false) => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    // Check if split bill is enabled
    if (isChecked) {
      // Collect all persons (added users + current input if filled)
      const allPersons = [...users];

      // Add current person in input fields if they have filled it
      if (customerName.trim() && customerPhone.trim()) {
        allPersons.push({
          name: customerName.trim(),
          phone: customerPhone.trim(),
        });
      }

      // Split bill mode - need at least one person
      if (allPersons.length === 0) {
        toast.error("Please add at least one person for split bill");
        return;
      }

      // Call split bill API
      try {
        const orderItems = cart.map((item) => ({
          item_id: item.id,
          item_name: item.name,
          quantity: item.quantity,
          price: getItemPrice(item),
          modifiers: item.modifiers || [],
          add_ons: item.add_ons || [],
          notes: item.notes || "",
        }));

        const response = await axios.post(`${API}/orders/customer/split`, {
          table_id: tableId,
          order_type: "dine_in",
          items: orderItems,
          split_bill: true,
          split_persons: allPersons,
        });

        toast.success(
          `Split bill created! ${response.data.total_orders} orders generated.`
        );
        // Navigate to split order success page
        navigate(
          `/order-success/${tableId}/${response.data.parent_order_id}?split=true`
        );
        setCart([]);
        setUsers([]);
        setCustomerName("");
        setCustomerPhone("");
        return;
      } catch (error) {
        console.error("Error creating split bill:", error);
        toast.error("Failed to create split bill. Please try again.");
        return;
      }
    }

    // Normal single order flow
    if (!existingOrderId && (!customerName || !customerPhone)) {
      toast.error("Please enter your name and phone number");
      return;
    }

    if (!existingOrderId && !isValidPhoneNumber(customerPhone)) {
      toast.error("Phone number must be at least 10 digits");
      return;
    }

    if (!skipUpsellCheck && !existingOrderId) {
      const cartCategories = [...new Set(cart.map((item) => item.category_id))];
      try {
        const response = await axios.post(`${API}/upsell-rules/check`, {
          items: cart.map((item) => ({
            item_id: item.id,
            category_id: item.category_id,
            quantity: item.quantity,
          })),
        });

        if (response.data && response.data.length > 0) {
          setUpsellOffers(response.data);
          setUpsellDialogOpen(true);
          return;
        }
      } catch (error) {
        console.log("No upsell offers available");
      }
    }

    try {
      const orderItems = cart.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        quantity: item.quantity,
        price: getItemPrice(item),
        modifiers: item.modifiers || [],
        add_ons: item.add_ons || [],
        notes: item.notes || "",
      }));

      if (existingOrderId) {
        // Add items to existing order
        const response = await axios.put(
          `${API}/public/orders/${existingOrderId}/add-items?table_id=${tableId}`,
          orderItems
        );
        toast.success("Items added to your order!");
        navigate(`/order-success/${tableId}/${existingOrderId}`);
      } else {
        // Create new order
        const response = await axios.post(`${API}/orders/customer`, {
          table_id: tableId,
          order_type: "dine_in",
          items: orderItems,
          customer_name: customerName,
          customer_phone: customerPhone,
          total: calculateCartTotal(),
        });

        toast.success("Order placed successfully!");
        navigate(`/order-success/${tableId}/${response.data.id}`);
      }

      setCart([]);
      setUpsellDialogOpen(false);
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order. Please try again.");
    }
  };

  const filteredMenuItems = menuItems;

  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{10,}$/;
    const cleanPhone = phone.replace(/[^0-9]/g, "");
    return phoneRegex.test(cleanPhone);
  };

  const handleAddUser = () => {
    if (!customerName.trim() || !customerPhone.trim()) {
      toast.error("Please enter name and phone");
      return;
    }

    if (!isValidPhoneNumber(customerPhone)) {
      toast.error("Phone number must be at least 10 digits");
      return;
    }

    setUsers([
      ...users,
      {
        id: Date.now(), // unique
        name: customerName,
        phone: customerPhone,
      },
    ]);

    // reset inputs ONLY
    setCustomerName("");
    setCustomerPhone("");
  };

  const handleRemoveUser = (id) => {
    setUsers((prev) => prev.filter((user) => user.id !== id));
  };
  console.log(users, "data");

  const groupedItems = categories.reduce((acc, cat) => {
    const categoryItems = filteredMenuItems.filter(
      (item) => item.category_id === cat.id
    );

    const subGrouped = categoryItems.reduce((subAcc, item) => {
      const key = item.sub_category_id || "no-sub";
      if (!subAcc[key]) subAcc[key] = [];
      subAcc[key].push(item);
      return subAcc;
    }, {});

    acc[cat.id] = subGrouped;
    return acc;
  }, {});

  const filteredCategories = categories.filter((cat) => {
    const subGroups = groupedItems[cat.id];
    return subGroups
      ? Object.values(subGroups).some((items) => items.length > 0)
      : false;
  });

  const canGoLeft = startIndex > 0;
  const canGoRight = startIndex + visibleCount < filteredCategories.length;

  const visibleCategories = filteredCategories.slice(
    startIndex,
    startIndex + visibleCount
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-20">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-6 shadow-lg">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold text-center">WaiterMan</h1>
          {table && (
            <p className="text-center text-blue-100 mt-2">
              Table {table.table_name}
            </p>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="shadow-xl border-2 border-blue-100">
              <CardHeader className="bg-gradient-to-r pb-0 from-blue-50 sticky top-0 z-50 to-purple-50">
                <CardTitle className="text-2xl font-bold text-gray-800">
                  Menu
                </CardTitle>
                <div className="relative mb-6 flex items-center gap-2 border-b overflow-hidden">
                  {/* ALL */}
                  <button
                    onClick={() => {
                      setSelectedCategory("all");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={`px-4 py-2 text-sm font-medium whitespace-nowrap
      border-b-4 transition-colors
      ${
        selectedCategory === "all"
          ? "border-blue-600 text-blue-600"
          : "border-transparent text-gray-500 hover:text-blue-600"
      }`}
                  >
                    All
                  </button>

                  {/* LEFT */}
                  {visibleCategories?.length >= visibleCount && (
                    <button
                      disabled={!canGoLeft}
                      onClick={() =>
                        setStartIndex((p) => Math.max(p - visibleCount, 0))
                      }
                      className={`p-1 rounded-full
      ${
        canGoLeft
          ? "bg-blue-600 text-white"
          : "bg-gray-200 text-white cursor-not-allowed"
      }`}
                    >
                      <ChevronLeft size={18} />
                    </button>
                  )}
                  {/* CATEGORY LIST */}
                  <div className="flex gap-4 overflow-x-auto scrollbar-hide max-w-full">
                    {visibleCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => {
                          setSelectedCategory(cat.id);
                          document
                            .getElementById(`category-${cat.id}`)
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }}
                        className={`px-2 py-2 text-sm font-medium whitespace-nowrap
          border-b-4 transition-colors
          ${
            selectedCategory === cat.id
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-blue-600"
          }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>

                  {/* RIGHT */}
                  {visibleCategories?.length >= visibleCount && (
                    <button
                      disabled={!canGoRight}
                      onClick={() =>
                        setStartIndex((p) =>
                          Math.min(
                            p + visibleCount,
                            filteredCategories.length - visibleCount
                          )
                        )
                      }
                      className={`p-1 rounded-full
      ${
        canGoRight
          ? "bg-blue-600 text-white"
          : "bg-gray-200 text-white cursor-not-allowed"
      }`}
                    >
                      <ChevronRight size={18} />
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6 ">
                {subCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6 pl-4">
                    <Button
                      variant={
                        selectedSubCategory === "all" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => setSelectedSubCategory("all")}
                    >
                      All Items
                    </Button>
                    {subCategories.map((subCat) => (
                      <Button
                        key={subCat.id}
                        variant={
                          selectedSubCategory === subCat.id
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => setSelectedSubCategory(subCat.id)}
                      >
                        {subCat.name}
                      </Button>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6">
                  {Object.entries(groupedItems).map(
                    ([categoryId, subGroups]) => {
                      const hasItems = Object.values(subGroups).some(
                        (items) => items.length > 0
                      );

                      if (!hasItems) return null;

                      return (
                        <div key={categoryId} className="mb-10">
                          {/* CATEGORY TITLE */}
                          <h3
                            id={`category-${categoryId}`}
                            className="font-bold text-3xl text-gray-800 mb-6 scroll-mt-28"
                          >
                            {
                              categories.find((cat) => cat.id === categoryId)
                                ?.name
                            }
                          </h3>

                          {/* SUB CATEGORIES */}
                          {Object.entries(subGroups).map(([subId, items]) => {
                            if (items.length === 0) return null;

                            return (
                              <div key={subId} className="mb-6">
                                {subId !== "no-sub" && (
                                  <h4 className="text-xl font-semibold text-gray-700 mb-4 pl-1">
                                    Sub Category
                                  </h4>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {items.map((item) => (
                                    <Card
                                      key={item.id}
                                      className="hover:shadow-lg transition-shadow border border-gray-200"
                                    >
                                      <CardContent className="p-4">
                                        <h3 className="font-bold text-lg text-gray-800 mb-1">
                                          {item.name}
                                        </h3>

                                        {item.description && (
                                          <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                                            {item.description}
                                          </p>
                                        )}

                                        <p className="text-xl font-bold text-blue-600 mb-3">
                                          ${getItemPrice(item)}
                                        </p>

                                        <Button
                                          onClick={() => addToCart(item)}
                                          className="w-full bg-blue-600 hover:bg-blue-700"
                                        >
                                          <Plus size={16} className="mr-2" />
                                          Add
                                        </Button>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            );
                          })}

                          <div className="border-t border-gray-100 mt-6"></div>
                        </div>
                      );
                    }
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="shadow-xl border-2 border-purple-100 sticky top-4">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
                <CardTitle className="flex items-center text-2xl font-bold text-gray-800">
                  <ShoppingCart className="mr-2" /> Your Cart
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {cart.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    Cart is empty
                  </p>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div
                        key={item.cart_item_id || item.id}
                        className="border-b pb-4"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">
                              {item.name}
                            </h4>
                            {item.modifiers && item.modifiers.length > 0 && (
                              <div className="text-xs text-gray-600 mt-1">
                                {item.modifiers.map((mod, idx) => (
                                  <div key={idx}>
                                    {mod.group_name}: {mod.option_name}
                                  </div>
                                ))}
                              </div>
                            )}
                            {item.add_ons && item.add_ons.length > 0 && (
                              <div className="text-xs text-gray-600 mt-1">
                                Add-ons:{" "}
                                {item.add_ons.map((a) => a.name).join(", ")}
                              </div>
                            )}
                            {item.notes && (
                              <div className="text-xs text-gray-600 mt-1 italic">
                                Note: {item.notes}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              removeFromCart(item.cart_item_id || item.id)
                            }
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity - 1)
                              }
                              className="h-8 w-8 p-0"
                            >
                              <Minus size={14} />
                            </Button>
                            <span className="font-semibold w-8 text-center">
                              {item.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity + 1)
                              }
                              className="h-8 w-8 p-0"
                            >
                              <Plus size={14} />
                            </Button>
                          </div>
                          <span className="font-bold text-blue-600">
                            ${(getItemPrice(item) * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}

                    <div className="pt-4 border-t-2">
                      <div className="flex justify-between items-center text-xl font-bold mb-4">
                        <span>Total:</span>
                        <span className="text-blue-600">
                          ${calculateCartTotal().toFixed(2)}
                        </span>
                      </div>
                      <div className="flex mb-4 items-center gap-2">
                        <input
                          id="splitBill"
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            setIsChecked(e.target.checked);
                            if (!e.target.checked) {
                              setCustomerName("");
                              setCustomerPhone("");
                              setUsers([]);
                            }
                          }}
                          className="w-5 h-5 accent-blue-600 cursor-pointer"
                        />

                        <span className="text-xl font-bold select-none">
                          Split Bill
                        </span>
                      </div>

                      {users.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {users.map((user) => (
                            <div
                              key={user.id}
                              className="relative space-y-3 border-2 p-4 rounded-lg mt-4"
                            >
                              <button
                                onClick={() => handleRemoveUser(user.id)}
                                className="absolute top-2 right-2 w-6 h-6 rounded-3xl bg-red-500 text-white hover:text-white hover:bg-red-600"
                              >
                                ✕
                              </button>

                              <div>
                                <Label>Your Name *</Label>
                                <Input
                                  value={user.name}
                                  readOnly
                                  placeholder="Enter your name"
                                />
                              </div>

                              <div>
                                <Label>Phone Number *</Label>
                                <Input
                                  value={user.phone}
                                  readOnly
                                  placeholder="Enter phone number"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {isChecked ? (
                        <div className="relative space-y-3 border-2 p-4 rounded-lg mt-4">

                          <div>
                            <Label>Your Name *</Label>
                            <Input
                              value={customerName}
                              onChange={(e) => setCustomerName(e.target.value)}
                              placeholder="Enter your name"
                            />
                          </div>

                          <div>
                            <Label>Phone Number *</Label>
                            <Input
                              value={customerPhone}
                              onChange={(e) => setCustomerPhone(e.target.value)}
                              placeholder="Enter phone number"
                            />
                          </div>

                          <Button
                            type="button"
                            onClick={handleAddUser}
                            className="w-max mt-3 bg-gradient-to-r from-blue-600 to-purple-600"
                          >
                            Add User
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="name">Your Name *</Label>
                            <Input
                              id="name"
                              value={customerName}
                              onChange={(e) => setCustomerName(e.target.value)}
                              placeholder="Enter your name"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="phone">Phone Number *</Label>
                            <Input
                              id="phone"
                              value={customerPhone}
                              onChange={(e) => setCustomerPhone(e.target.value)}
                              placeholder="Enter phone number"
                              className="mt-1"
                            />
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={() => placeOrder(false)}
                        className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg py-6"
                      >
                        Place Order
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ModifierDialog
        open={modifierDialogOpen}
        onOpenChange={setModifierDialogOpen}
        item={selectedMenuItem}
        onAddToCart={addToCartWithModifiers}
      />

      <Dialog open={upsellDialogOpen} onOpenChange={setUpsellDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Special Offers for You!</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {upsellOffers.map((offer, index) => (
              <div
                key={index}
                className="p-4 bg-green-50 rounded-lg border border-green-200"
              >
                <h3 className="font-bold text-lg text-green-800 mb-2">
                  {offer.rule_name}
                </h3>
                <p className="text-gray-700 mb-3">{offer.description}</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      toast.success("Offer will be applied at checkout");
                      placeOrder(true);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Accept Offer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      placeOrder(true);
                    }}
                  >
                    No Thanks
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Waiter Voice Chat */}
      <AIWaiterVoiceChat
        tableId={tableId}
        restaurantId="default"
        onOrderComplete={(orderData) => {
          toast.success("Your AI order has been placed!");
        }}
      />
    </div>
  );
}
